import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Input } from 'src/design/atoms/Input';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';
import {
  CostCodeDto,
  CostCodeRequestDto,
  OrganizationOfficeDto,
} from 'src/api/FiretrolApiClient';
import {
  useCreateCostCode,
  useUpdateCostCode,
} from 'src/api/queries/organization/useCostCodeMutations';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorMulti';
import { ModalButton } from 'src/design/molecules/ModalButton';
import * as Yup from 'yup';
import { getErrorMessage, showErrorToast } from 'src/utils';

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .required('Code is required')
    .min(1, 'Code must not be empty')
    .max(50, 'Code must be 50 characters or less')
    .label('Code'),
  name: Yup.string()
    .required('Name is required')
    .min(1, 'Name must not be empty')
    .max(100, 'Name must be 100 characters or less')
    .label('Name'),
});

export interface ICostCodeFormValues {
  id: string;
  code: string;
  name: string;
  offices: OrganizationOfficeDto[];
}

const initialValues: ICostCodeFormValues = {
  id: '',
  code: '',
  name: '',
  offices: [],
};

export interface ICreateCostCodeFormProps {
  selectedCostCode?: CostCodeDto;
  setCreateCostCodeDialogOpen: (
    isOpen: boolean,
    costCode?: CostCodeDto,
  ) => void;
}

export const CreateCostCodeForm: React.FC<ICreateCostCodeFormProps> = ({
  selectedCostCode,
  setCreateCostCodeDialogOpen,
}) => {
  const createCostCode = useCreateCostCode();
  const updateCostCode = useUpdateCostCode();
  const [error, setError] = React.useState<string | null>(null);

  const closeDialog = () => {
    setCreateCostCodeDialogOpen(false);
    formik.resetForm();
  };

  const getInitialValues = (): ICostCodeFormValues => {
    if (selectedCostCode) {
      return {
        id: selectedCostCode.id,
        code: selectedCostCode.code,
        name: selectedCostCode.name,
        offices: selectedCostCode.offices ?? [],
      };
    }
    return initialValues;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        const requestData: CostCodeRequestDto = {
          code: values.code,
          name: values.name,
          offices: values.offices.map((office) => ({
            organizationOfficeId: office.id,
          })),
        };

        if (selectedCostCode) {
          await updateCostCode.mutateAsync({
            id: selectedCostCode.id,
            request: requestData,
          });
        } else {
          await createCostCode.mutateAsync(requestData);
        }
        setCreateCostCodeDialogOpen(false);
      } catch (err: unknown) {
        const msg = getErrorMessage(err);
        setError(msg);
        showErrorToast(msg);
      }
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <Stack gap="md">
        <TextInput
          label="Code"
          placeholder="Enter cost code (e.g., 1000, LABOR, etc.)"
          withAsterisk
          value={formik.values.code}
          onChange={(e) => formik.setFieldValue('code', e.target.value)}
          onBlur={formik.handleBlur}
          error={formik.touched.code && formik.errors.code}
          onKeyDown={handleKeyDown}
          description="A unique identifier for this cost code"
        />

        <TextInput
          label="Name"
          placeholder="Enter cost code name"
          withAsterisk
          value={formik.values.name}
          onChange={(e) => formik.setFieldValue('name', e.target.value)}
          onBlur={formik.handleBlur}
          error={formik.touched.name && formik.errors.name}
          onKeyDown={handleKeyDown}
          description="A descriptive name for this cost code"
        />

        <Input.Wrapper
          label="Offices"
          description="Select one or more offices to associate with this cost code. If no offices are selected, the cost code will be available to all offices."
        >
          <OfficeSelector
            onChange={(offices) => formik.setFieldValue('offices', offices)}
            initialValue={formik.values.offices}
          />
        </Input.Wrapper>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}
      </Stack>

      <Group justify="flex-end" mt="xl">
        <ModalButton onClick={closeDialog} color="dark" variant="light">
          Cancel
        </ModalButton>
        <ModalButton
          onClick={() => formik.handleSubmit()}
          loading={formik.isSubmitting}
          disabled={!formik.isValid || !formik.dirty}
        >
          {selectedCostCode ? 'Save Changes' : 'Create Cost Code'}
        </ModalButton>
      </Group>
    </>
  );
};
